import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AdminImageUpload } from '../../components/AdminImageUpload'
import { adminApi } from '../../lib/api'
import type { AdminData } from '../../types/commerce'

const emptyNavigation = { id: '', label: '', href: '', active: true, position: 0 }

export function AdminContent() {
  const [data, setData] = useState<AdminData | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [navigation, setNavigation] = useState(emptyNavigation)
  const [category, setCategory] = useState({ id: '', name: '', description: '', image_path: null as string | null, active: true, position: 0 })
  const [collection, setCollection] = useState({ id: '', name: '', description: '', image_path: null as string | null, active: true, position: 0 })
  const [banner, setBanner] = useState({ id: '', placement: 'hero', eyebrow: '', title: '', subtitle: '', image_path: null as string | null, mobile_image_path: null as string | null, link_label: '', link_url: '', active: true, position: 0 })
  const [coverImage, setCoverImage] = useState<string | null | undefined>(undefined)
  const [coverMobileImage, setCoverMobileImage] = useState<string | null | undefined>(undefined)
  const [savingCover, setSavingCover] = useState(false)
  const currentCover = data?.banners.filter(item => item.placement === 'hero')
    .sort((a, b) => a.position - b.position)[0]
  const coverDesktopPath = coverImage === undefined ? currentCover?.image_path ?? null : coverImage
  const coverMobilePath = coverMobileImage === undefined ? currentCover?.mobile_image_path ?? null : coverMobileImage

  const refresh = () => adminApi.data().then(setData).catch(err => setError(err.message))
  useEffect(() => { void refresh() }, [])

  const done = (message = 'Alteração salva.') => {
    setError('')
    setNotice(message)
    void refresh()
    setTimeout(() => setNotice(''), 2500)
  }

  const saveCover = async () => {
    setError(''); setNotice('')
    if (!coverDesktopPath) {
      setError('Selecione uma imagem para a capa principal antes de salvar.')
      return
    }
    setSavingCover(true)
    try {
      await adminApi.saveBanner({
        id: currentCover?.id ?? '',
        placement: 'hero',
        eyebrow: currentCover?.eyebrow ?? 'GK MAISON',
        title: currentCover?.title ?? 'ELEGÂNCIA ESSENCIAL',
        subtitle: currentCover?.subtitle ?? 'Conheça nossa coleção',
        image_path: coverDesktopPath,
        mobile_image_path: coverMobilePath,
        link_label: currentCover?.link_label ?? 'VER COLEÇÃO',
        link_url: currentCover?.link_url ?? '/produtos',
        active: true,
        position: currentCover?.position ?? -100
      })
      setCoverImage(undefined)
      setCoverMobileImage(undefined)
      done('Foto da capa atualizada com sucesso.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar a capa.')
    } finally {
      setSavingCover(false)
    }
  }

  const removeNavigation = () => {
    if (!navigation.id || !window.confirm(`Excluir "${navigation.label}" do menu da loja?`)) return
    adminApi.deleteNavigationLink(navigation.id)
      .then(() => {
        setNavigation(emptyNavigation)
        done('Item removido do menu.')
      })
      .catch(err => setError(err.message))
  }

  return <div className="admin-page">
    <div className="admin-page__heading">
      <div><span>VITRINE</span><h1>Menu, categorias, coleções e banners</h1><p>Controle o que aparece na navegação e na vitrine da loja sem precisar alterar código.</p></div>
    </div>
    {error && <div className="form-error">{error}</div>}
    {notice && <div className="form-success">{notice}</div>}

    <section className="admin-panel admin-form__section admin-banner-panel">
      <div className="panel-title-row">
        <div>
          <h2>Foto da capa do site</h2>
          <p>Escolha ou substitua a imagem principal que aparece no início da loja. A foto para celular é opcional.</p>
        </div>
      </div>
      <div className="form-grid">
        <AdminImageUpload label="Foto da capa (computador)" value={coverDesktopPath}
          onChange={setCoverImage} folder="banners/capa" />
        <AdminImageUpload label="Foto da capa (celular, opcional)" value={coverMobilePath}
          onChange={setCoverMobileImage} folder="banners/capa" />
      </div>
      <p className="server-note">A imagem será exibida na página inicial após salvar. Para editar o título ou o botão da capa, utilize a seção Banners mais abaixo.</p>
      <div className="panel-actions">
        <button type="button" className="button button--dark"
          disabled={!coverDesktopPath || savingCover || !data}
          onClick={() => void saveCover()}>
          {savingCover ? 'SALVANDO CAPA...' : 'SALVAR FOTO DA CAPA'}
        </button>
      </div>
    </section>

    <section className="admin-panel admin-form__section admin-menu-panel">
      <div className="panel-title-row">
        <div><h2>Menu da loja</h2><p>Os itens ativos aparecem no topo e no menu mobile. Selecione um item para editar, desativar ou excluir.</p></div>
      </div>
      <div className="admin-menu-links">
        {data?.navigation_links.map(item => (
          <button
            key={item.id}
            type="button"
            className={`${navigation.id === item.id ? 'is-active' : ''} ${!item.active ? 'is-inactive' : ''}`}
            onClick={() => setNavigation({ id: item.id, label: item.label, href: item.href, active: item.active, position: item.position })}
          >
            <strong>{item.label}</strong>
            <span>{item.active ? 'Ativo' : 'Oculto'}</span>
          </button>
        ))}
      </div>
      {!data?.navigation_links.length && <div className="admin-menu-empty">Nenhum item configurado. Crie o primeiro abaixo.</div>}
      <div className="form-grid">
        <label>Nome<input value={navigation.label} onChange={e => setNavigation(x => ({ ...x, label: e.target.value }))} placeholder="Ex.: FEMININO" /></label>
        <label>Link<input value={navigation.href} onChange={e => setNavigation(x => ({ ...x, href: e.target.value }))} placeholder="Ex.: /produtos?gender=feminino" /></label>
      </div>
      <div className="form-grid">
        <label>Ordem<input type="number" value={navigation.position} onChange={e => setNavigation(x => ({ ...x, position: Number(e.target.value) }))} /></label>
        <label className="inline-check"><input type="checkbox" checked={navigation.active} onChange={e => setNavigation(x => ({ ...x, active: e.target.checked }))} /> Mostrar no menu</label>
      </div>
      <div className="panel-actions panel-actions--menu">
        <button className="button button--outline" type="button" onClick={() => setNavigation(emptyNavigation)}>NOVO</button>
        {navigation.id && <button className="button admin-delete-button" type="button" onClick={removeNavigation}><Trash2 size={15} /> EXCLUIR</button>}
        <button className="button button--dark" type="button" disabled={!navigation.label.trim() || !navigation.href.trim()} onClick={() => adminApi.saveNavigationLink(navigation).then(() => done()).catch(err => setError(err.message))}>SALVAR</button>
      </div>
    </section>

    <div className="admin-content-grid">
      <section className="admin-panel admin-form__section">
        <h2>Categorias</h2>
        <div className="chip-list">{data?.categories.map(c => <button key={c.id} onClick={() => setCategory({ id: c.id, name: c.name, description: c.description ?? '', image_path: c.image_path, active: c.active, position: c.position })}>{c.name}</button>)}</div>
        <label>Nome<input value={category.name} onChange={e => setCategory(x => ({ ...x, name: e.target.value }))} /></label>
        <label>Descrição<textarea rows={3} value={category.description} onChange={e => setCategory(x => ({ ...x, description: e.target.value }))} /></label>
        <AdminImageUpload value={category.image_path} onChange={image_path => setCategory(x => ({ ...x, image_path }))} folder="categories" />
        <div className="form-grid"><label>Posição<input type="number" value={category.position} onChange={e => setCategory(x => ({ ...x, position: Number(e.target.value) }))} /></label><label className="inline-check"><input type="checkbox" checked={category.active} onChange={e => setCategory(x => ({ ...x, active: e.target.checked }))} /> Ativa</label></div>
        <div className="panel-actions"><button className="button button--outline" onClick={() => setCategory({ id: '', name: '', description: '', image_path: null, active: true, position: 0 })}>NOVA</button><button className="button button--dark" onClick={() => adminApi.saveCategory(category).then(() => done()).catch(err => setError(err.message))}>SALVAR</button></div>
      </section>

      <section className="admin-panel admin-form__section">
        <h2>Coleções</h2>
        <div className="chip-list">{data?.collections.map(c => <button key={c.id} onClick={() => setCollection({ id: c.id, name: c.name, description: c.description ?? '', image_path: c.image_path, active: c.active, position: c.position })}>{c.name}</button>)}</div>
        <label>Nome<input value={collection.name} onChange={e => setCollection(x => ({ ...x, name: e.target.value }))} /></label>
        <label>Descrição<textarea rows={3} value={collection.description} onChange={e => setCollection(x => ({ ...x, description: e.target.value }))} /></label>
        <AdminImageUpload value={collection.image_path} onChange={image_path => setCollection(x => ({ ...x, image_path }))} folder="collections" />
        <div className="form-grid"><label>Posição<input type="number" value={collection.position} onChange={e => setCollection(x => ({ ...x, position: Number(e.target.value) }))} /></label><label className="inline-check"><input type="checkbox" checked={collection.active} onChange={e => setCollection(x => ({ ...x, active: e.target.checked }))} /> Ativa</label></div>
        <div className="panel-actions"><button className="button button--outline" onClick={() => setCollection({ id: '', name: '', description: '', image_path: null, active: true, position: 0 })}>NOVA</button><button className="button button--dark" onClick={() => adminApi.saveCollection(collection).then(() => done()).catch(err => setError(err.message))}>SALVAR</button></div>
      </section>
    </div>

    <section className="admin-panel admin-form__section admin-banner-panel">
      <h2>Banners</h2>
      <div className="chip-list">{data?.banners.map(b => <button key={b.id} onClick={() => setBanner({ id: b.id, placement: b.placement, eyebrow: b.eyebrow ?? '', title: b.title, subtitle: b.subtitle ?? '', image_path: b.image_path, mobile_image_path: b.mobile_image_path, link_label: b.link_label ?? '', link_url: b.link_url ?? '', active: b.active, position: b.position })}>{b.title}</button>)}</div>
      <div className="form-grid"><label>Posição<select value={banner.placement} onChange={e => setBanner(x => ({ ...x, placement: e.target.value }))}><option value="hero">Capa principal</option><option value="editorial">Editorial</option><option value="collection">Coleção</option></select></label><label>Ordem<input type="number" value={banner.position} onChange={e => setBanner(x => ({ ...x, position: Number(e.target.value) }))} /></label></div>
      <label>Eyebrow<input value={banner.eyebrow} onChange={e => setBanner(x => ({ ...x, eyebrow: e.target.value }))} /></label>
      <label>Título<input value={banner.title} onChange={e => setBanner(x => ({ ...x, title: e.target.value }))} /></label>
      <label>Subtítulo<textarea rows={3} value={banner.subtitle} onChange={e => setBanner(x => ({ ...x, subtitle: e.target.value }))} /></label>
      <div className="form-grid"><AdminImageUpload label="Imagem desktop" value={banner.image_path} onChange={image_path => setBanner(x => ({ ...x, image_path }))} folder="banners" /><AdminImageUpload label="Imagem mobile" value={banner.mobile_image_path} onChange={mobile_image_path => setBanner(x => ({ ...x, mobile_image_path }))} folder="banners" /></div>
      <div className="form-grid"><label>Texto do botão<input value={banner.link_label} onChange={e => setBanner(x => ({ ...x, link_label: e.target.value }))} /></label><label>Link<input value={banner.link_url} onChange={e => setBanner(x => ({ ...x, link_url: e.target.value }))} /></label></div>
      <label className="inline-check"><input type="checkbox" checked={banner.active} onChange={e => setBanner(x => ({ ...x, active: e.target.checked }))} /> Ativo</label>
      <div className="panel-actions"><button className="button button--outline" onClick={() => setBanner({ id: '', placement: 'hero', eyebrow: '', title: '', subtitle: '', image_path: null, mobile_image_path: null, link_label: '', link_url: '', active: true, position: 0 })}>NOVO</button><button className="button button--dark" disabled={!banner.image_path || !banner.title} onClick={() => adminApi.saveBanner(banner).then(() => done()).catch(err => setError(err.message))}>SALVAR</button></div>
    </section>
  </div>
}
